import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useNavigate } from 'react-router-dom';
import { Users, Search, ChevronRight, FileCheck, FileX, Calendar, Upload, CheckCircle, Clock, AlertTriangle, BarChart3, LayoutGrid, ArrowUpRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MONTHS = [
  { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
  { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
  { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
  { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' }
];

const formatCurrency = (value) => (value || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0, maximumFractionDigits: 0 });

function renderListState(loading, isEmpty, loadingContent, emptyContent, children) {
  if (loading) return loadingContent;
  if (isEmpty) return emptyContent;
  return children;
}

// ─── Health View Components ───

const HealthDistributionBar = ({ overview }) => {
  const { total, filed = 0, filed_delayed = 0, ready = 0, in_progress = 0, not_started = 0 } = overview;
  if (total === 0) return null;
  const filedPct = (filed / total) * 100;
  const delayedPct = (filed_delayed / total) * 100;
  const readyPct = (ready / total) * 100;
  const progressPct = (in_progress / total) * 100;
  const notStartedPct = (not_started / total) * 100;

  return (
    <div data-testid="health-distribution-bar">
      <div className="flex items-center gap-5 mb-3 flex-wrap">
        {filed > 0 && <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm bg-emerald-600" />
          <span className="text-xs text-slate-600">Filed <span className="font-mono font-semibold text-slate-800">{filed}</span></span>
        </div>}
        {filed_delayed > 0 && <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm bg-red-500" />
          <span className="text-xs text-slate-600">Delayed <span className="font-mono font-semibold text-slate-800">{filed_delayed}</span></span>
        </div>}
        {ready > 0 && <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm bg-emerald-400" />
          <span className="text-xs text-slate-600">Ready <span className="font-mono font-semibold text-slate-800">{ready}</span></span>
        </div>}
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm bg-amber-400" />
          <span className="text-xs text-slate-600">In Progress <span className="font-mono font-semibold text-slate-800">{in_progress}</span></span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm bg-slate-300" />
          <span className="text-xs text-slate-600">Not Started <span className="font-mono font-semibold text-slate-800">{not_started}</span></span>
        </div>
      </div>
      <div className="w-full h-3 bg-slate-100 rounded-sm overflow-hidden flex" data-testid="health-bar-visual">
        {filedPct > 0 && <div className="bg-emerald-600 h-full transition-all duration-500" style={{ width: `${filedPct}%` }} />}
        {delayedPct > 0 && <div className="bg-red-500 h-full transition-all duration-500" style={{ width: `${delayedPct}%` }} />}
        {readyPct > 0 && <div className="bg-emerald-400 h-full transition-all duration-500" style={{ width: `${readyPct}%` }} />}
        {progressPct > 0 && <div className="bg-amber-400 h-full transition-all duration-500" style={{ width: `${progressPct}%` }} />}
        {notStartedPct > 0 && <div className="bg-slate-300 h-full transition-all duration-500" style={{ width: `${notStartedPct}%` }} />}
      </div>
      <div className="flex justify-between mt-1.5">
        <span className="text-[11px] font-mono text-slate-400">{total} clients</span>
        <span className="text-[11px] font-mono text-emerald-600 font-semibold">{Math.round(((filed + filed_delayed) / total) * 100)}% filed</span>
      </div>
    </div>
  );
};

const StepDot = ({ done, label, notApplicable }) => {
  if (notApplicable) {
    return (
      <span className="text-[10px] text-slate-300 flex items-center gap-0.5" title={`${label}: N/A`}>
        <span className="w-1.5 h-1.5 rounded-full bg-slate-200" />
      </span>
    );
  }
  return (
    <span
      className={`text-[10px] flex items-center gap-0.5 ${done ? 'text-emerald-600' : 'text-slate-400'}`}
      title={`${label}: ${done ? 'Done' : 'Pending'}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${done ? 'bg-emerald-500' : 'bg-slate-300'}`} />
    </span>
  );
};

function getStatusStyle(status) {
  switch (status) {
    case 'filed': return { barColor: 'bg-emerald-600', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'Filed', Icon: CheckCircle };
    case 'filed_delayed': return { barColor: 'bg-red-500', badge: 'bg-red-50 text-red-700 border-red-300', label: 'Delayed', Icon: AlertTriangle };
    case 'ready': return { barColor: 'bg-emerald-400', badge: 'bg-emerald-50 text-emerald-600 border-emerald-200', label: 'Ready', Icon: CheckCircle };
    case 'in_progress': return { barColor: 'bg-amber-400', badge: 'bg-amber-50 text-amber-700 border-amber-200', label: 'In Progress', Icon: AlertTriangle };
    default: return { barColor: 'bg-slate-300', badge: 'bg-slate-50 text-slate-500 border-slate-200', label: 'Not Started', Icon: Clock };
  }
}

const HealthTable = ({ clients, navigate, selectedYear, selectedMonth }) => (
  <div className="overflow-x-auto" data-testid="health-table">
    <table className="w-full">
      <thead>
        <tr className="bg-slate-50 border-b border-slate-200">
          <th className="text-left text-xs font-semibold uppercase text-slate-500 tracking-wider py-2.5 px-4">Client</th>
          <th className="text-left text-xs font-semibold uppercase text-slate-500 tracking-wider py-2.5 px-3">GSTIN</th>
          <th className="text-center text-xs font-semibold uppercase text-slate-500 tracking-wider py-2.5 px-3">Sales</th>
          <th className="text-center text-xs font-semibold uppercase text-slate-500 tracking-wider py-2.5 px-3">E-Invoice</th>
          <th className="text-center text-xs font-semibold uppercase text-slate-500 tracking-wider py-2.5 px-2">GSTR-1</th>
          <th className="text-center text-xs font-semibold uppercase text-slate-500 tracking-wider py-2.5 px-2">GSTR-3B</th>
          <th className="text-left text-xs font-semibold uppercase text-slate-500 tracking-wider py-2.5 px-3 w-36">Progress</th>
          <th className="text-right text-xs font-semibold uppercase text-slate-500 tracking-wider py-2.5 px-3">Txns</th>
          <th className="text-right text-xs font-semibold uppercase text-slate-500 tracking-wider py-2.5 px-4">Taxable</th>
          <th className="text-center text-xs font-semibold uppercase text-slate-500 tracking-wider py-2.5 px-3">Status</th>
          <th className="py-2.5 px-3 w-8"></th>
        </tr>
      </thead>
      <tbody>
        {clients.map((c) => {
          const { barColor, badge, label, Icon } = getStatusStyle(c.status);
          const displayPct = (c.status === 'filed' || c.status === 'filed_delayed') ? 100 : c.completion_pct;

          return (
            <tr
              key={c.client_id}
              className={`border-b border-slate-100 hover:bg-slate-50/70 transition-colors cursor-pointer ${c.status === 'filed_delayed' ? 'bg-red-50/30' : ''}`}
              onClick={() => navigate(`/clients/${c.client_id}/filing/${selectedYear}/${selectedMonth}`)}
              data-testid={`health-row-${c.client_id}`}
            >
              <td className="py-2.5 px-4">
                <span className="text-sm font-medium text-slate-900 truncate block max-w-[200px]">{c.name}</span>
              </td>
              <td className="py-2.5 px-3">
                <span className="text-xs font-mono text-slate-500">{c.gstin}</span>
              </td>
              <td className="py-2.5 px-3 text-center">
                <StepDot done={c.steps.sales} label="Sales" />
              </td>
              <td className="py-2.5 px-3 text-center">
                <StepDot done={c.steps.einvoice} label="E-Invoice" notApplicable={c.steps.einvoice === null} />
              </td>
              <td className="py-2.5 px-2 text-center">
                <StepDot done={c.steps.gstr1_filed || c.gstr1_filed} label="GSTR-1 Filed" />
              </td>
              <td className="py-2.5 px-2 text-center">
                <StepDot done={c.steps.gstr3b_filed || c.gstr3b_filed} label="GSTR-3B Filed" />
              </td>
              <td className="py-2.5 px-3">
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-1.5 bg-slate-100 rounded-sm overflow-hidden">
                    <div className={`h-full ${barColor} transition-all duration-300`} style={{ width: `${displayPct}%` }} />
                  </div>
                  <span className="text-[11px] font-mono text-slate-500 w-8 text-right">{displayPct}%</span>
                </div>
              </td>
              <td className="py-2.5 px-3 text-right">
                <span className="text-sm font-mono text-slate-700">{c.transaction_count || '\u2013'}</span>
              </td>
              <td className="py-2.5 px-4 text-right">
                <span className="text-sm font-mono text-slate-700">{c.taxable_value ? formatCurrency(c.taxable_value) : '\u2013'}</span>
              </td>
              <td className="py-2.5 px-3 text-center">
                <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 border rounded-sm ${badge}`} data-testid={`health-status-${c.client_id}`}>
                  <Icon className="w-3 h-3" />
                  {label}
                </span>
              </td>
              <td className="py-2.5 px-3 text-center">
                <ArrowUpRight className="w-3.5 h-3.5 text-slate-300" />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

// ─── Main Dashboard ───

const Dashboard = () => {
  const [clients, setClients] = useState([]);
  const [filingStatus, setFilingStatus] = useState({});
  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [viewMode, setViewMode] = useState('cards'); // 'cards' | 'health'
  const [fetchError, setFetchError] = useState(null);
  const navigate = useNavigate();

  const years = useMemo(() => Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i), []);

  const fetchClients = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/clients`, { withCredentials: true });
      setClients(response.data);
      setFetchError(null);
    } catch (err) {
      setFetchError(err.response?.data?.detail || 'Failed to load clients');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchFilingStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/filing-status/${selectedYear}/${selectedMonth}`, { withCredentials: true });
      setFilingStatus(response.data);
    } catch (err) {
      setFilingStatus({});
      setFetchError(err.response?.data?.detail || 'Failed to load filing status');
    }
  }, [selectedYear, selectedMonth]);

  const fetchHealthData = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/dashboard/health/${selectedYear}/${selectedMonth}`, { withCredentials: true });
      setHealthData(response.data);
    } catch (err) {
      setHealthData(null);
      setFetchError(err.response?.data?.detail || 'Failed to load health data');
    }
  }, [selectedYear, selectedMonth]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  useEffect(() => {
    fetchFilingStatus();
    fetchHealthData();
  }, [fetchFilingStatus, fetchHealthData]);

  const filteredClients = useMemo(() => clients.filter(client =>
    client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.gstin.toLowerCase().includes(searchTerm.toLowerCase())
  ), [clients, searchTerm]);

  const filteredHealthClients = useMemo(() => {
    if (!healthData?.clients) return [];
    return healthData.clients.filter(c =>
      c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.gstin.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [healthData, searchTerm]);

  const stats = useMemo(() => {
    const total = clients.length;
    const gstr1Filed = clients.filter(c => filingStatus[c.id]?.gstr1_filed).length;
    const gstr3bFiled = clients.filter(c => filingStatus[c.id]?.gstr3b_filed).length;
    const bothFiled = clients.filter(c => filingStatus[c.id]?.both_filed).length;
    const pending = total - bothFiled;
    return { total, gstr1Filed, gstr3bFiled, pending };
  }, [clients, filingStatus]);

  const handleClientClick = (clientId) => {
    navigate(`/clients/${clientId}/filing/${selectedYear}/${selectedMonth}`);
  };

  const getClientStatus = (client) => {
    const fs = filingStatus[client.id];
    if (!fs) return { label: 'Not Started', color: 'slate', icon: Clock };
    if (fs.both_filed) {
      if (fs.any_delayed) return { label: 'Filed (Delayed)', color: 'red', icon: AlertTriangle };
      return { label: 'Filed', color: 'emerald', icon: CheckCircle };
    }
    if (fs.gstr1_filed || fs.gstr3b_filed) {
      return { label: 'Partially Filed', color: 'blue', icon: Upload };
    }
    if (fs.sales_uploaded && (!client.e_invoice_applicable || fs.einvoice_uploaded)) {
      return { label: 'Ready', color: 'green', icon: CheckCircle };
    }
    if (fs.sales_uploaded) {
      return { label: 'Sales Done', color: 'blue', icon: Upload };
    }
    return { label: 'Not Started', color: 'slate', icon: Clock };
  };

  const statusColors = {
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    red: 'bg-red-50 text-red-700 border-red-300',
    green: 'bg-green-50 text-green-700 border-green-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    slate: 'bg-slate-50 text-slate-500 border-slate-200',
  };

  const monthLabel = MONTHS.find(m => m.value === selectedMonth)?.label || '';

  return (
    <Layout>
      <div className="space-y-6" data-testid="dashboard-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>GST Filing Dashboard</h1>
            <p className="text-slate-500 mt-1">Select a client and period to manage GST returns</p>
          </div>
          {/* View Toggle */}
          <div className="flex items-center border border-slate-200 rounded-sm overflow-hidden" data-testid="view-mode-toggle">
            <button
              onClick={() => setViewMode('cards')}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors ${viewMode === 'cards' ? 'bg-slate-900 text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}
              data-testid="view-mode-cards"
            >
              <LayoutGrid className="w-3.5 h-3.5" /> Cards
            </button>
            <button
              onClick={() => setViewMode('health')}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors ${viewMode === 'health' ? 'bg-slate-900 text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}
              data-testid="view-mode-health"
            >
              <BarChart3 className="w-3.5 h-3.5" /> Health
            </button>
          </div>
        </div>

        {/* Period Selection */}
        <div className="bg-white border border-slate-200 rounded-sm p-5">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-slate-400" />
              <span className="text-sm font-medium text-slate-700">Filing Period:</span>
            </div>
            <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
              <SelectTrigger className="w-36 rounded-sm" data-testid="period-month-select"><SelectValue /></SelectTrigger>
              <SelectContent>{MONTHS.map(m => <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
              <SelectTrigger className="w-28 rounded-sm" data-testid="period-year-select"><SelectValue /></SelectTrigger>
              <SelectContent>{years.map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        {/* Filing Status Summary - shared between views */}
        {!loading && clients.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="filing-status-summary">
            <div className="bg-white border border-slate-200 rounded-sm p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">No. of Clients</p>
              <p className="text-2xl font-semibold font-mono text-slate-900 mt-1" data-testid="stat-total-clients">{stats.total}</p>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-emerald-600">GSTR-1 Filed</p>
              <p className="text-2xl font-semibold font-mono text-emerald-700 mt-1" data-testid="stat-gstr1-filed">{stats.gstr1Filed}<span className="text-sm font-normal text-emerald-500 ml-1">/ {stats.total}</span></p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-sm p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-blue-600">GSTR-3B Filed</p>
              <p className="text-2xl font-semibold font-mono text-blue-700 mt-1" data-testid="stat-gstr3b-filed">{stats.gstr3bFiled}<span className="text-sm font-normal text-blue-500 ml-1">/ {stats.total}</span></p>
            </div>
            <div className={`border rounded-sm p-4 ${stats.pending > 0 ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}>
              <p className={`text-xs font-semibold uppercase tracking-wide ${stats.pending > 0 ? 'text-amber-600' : 'text-green-600'}`}>Pending</p>
              <p className={`text-2xl font-semibold font-mono mt-1 ${stats.pending > 0 ? 'text-amber-700' : 'text-green-700'}`} data-testid="stat-pending">{stats.pending}</p>
            </div>
          </div>
        )}

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Search clients by name or GSTIN..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 rounded-sm" data-testid="client-search-input" />
        </div>

        {/* ─── HEALTH VIEW ─── */}
        {viewMode === 'health' && (
          <div className="space-y-4" data-testid="health-view">
            {/* Distribution Bar */}
            {healthData?.overview && (
              <div className="bg-white border border-slate-200 rounded-sm p-5">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-sm font-semibold text-slate-800 uppercase tracking-wide">Filing Health — {monthLabel} {selectedYear}</h2>
                </div>
                <HealthDistributionBar overview={healthData.overview} />
              </div>
            )}

            {/* Health Table */}
            <div className="bg-white border border-slate-200 rounded-sm">
              <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Client Filing Status</h3>
              </div>
              {renderListState(
                !healthData,
                healthData && filteredHealthClients.length === 0,
                <div className="p-8 text-center"><div className="spinner mx-auto mb-2" /><p className="text-slate-500">Loading health data...</p></div>,
                <div className="p-8 text-center text-slate-400 text-sm">No clients match your search</div>,
                <HealthTable clients={filteredHealthClients} navigate={navigate} selectedYear={selectedYear} selectedMonth={selectedMonth} />
              )}
            </div>
          </div>
        )}

        {/* ─── CARDS VIEW (original) ─── */}
        {viewMode === 'cards' && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {renderListState(
                loading,
                filteredClients.length === 0,
                <div className="col-span-full text-center py-12"><div className="spinner mx-auto mb-2" /><p className="text-slate-500">Loading clients...</p></div>,
                <div className="col-span-full text-center py-12">
                  <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No clients found</p>
                  <Button onClick={() => navigate('/clients')} className="mt-4 bg-[#0055FF] hover:bg-[#0044CC] text-white rounded-sm">Add Client</Button>
                </div>,
                filteredClients.map((client) => {
                  const cs = getClientStatus(client);
                  const fs = filingStatus[client.id];
                  const StatusIcon = cs.icon;
                  return (
                    <div
                      key={client.id}
                      onClick={() => handleClientClick(client.id)}
                      className="bg-white border border-slate-200 rounded-sm p-4 cursor-pointer hover:border-blue-300 hover:shadow-sm transition-all"
                      data-testid={`client-card-${client.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-slate-900 truncate">{client.name}</h3>
                          <p className="text-sm font-mono text-slate-500 mt-0.5">{client.gstin}</p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
                      </div>

                      {/* Tags row */}
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-600 rounded-sm">State: {client.state_code}</span>
                        {client.e_invoice_applicable ? (
                          <span className="text-xs px-2 py-0.5 bg-green-50 text-green-700 rounded-sm flex items-center gap-1">
                            <FileCheck className="w-3 h-3" />E-Inv
                          </span>
                        ) : (
                          <span className="text-xs px-2 py-0.5 bg-slate-50 text-slate-500 rounded-sm flex items-center gap-1">
                            <FileX className="w-3 h-3" />No E-Inv
                          </span>
                        )}
                      </div>

                      {/* Filing status for selected period */}
                      <div className="mt-3 pt-3 border-t border-slate-100">
                        <div className="flex items-center justify-between">
                          <span className={`text-xs px-2 py-1 border rounded-sm flex items-center gap-1.5 font-medium ${statusColors[cs.color]}`} data-testid={`filing-status-${client.id}`}>
                            <StatusIcon className="w-3.5 h-3.5" />
                            {monthLabel} {selectedYear}: {cs.label}
                          </span>
                          {fs && fs.transaction_count > 0 && (
                            <span className="text-xs text-slate-500 font-mono" data-testid={`txn-count-${client.id}`}>
                              {fs.transaction_count} txns
                            </span>
                          )}
                        </div>
                        {fs && fs.taxable_value > 0 && (
                          <p className="text-xs text-slate-400 font-mono mt-1.5" data-testid={`taxable-value-${client.id}`}>
                            Taxable: {formatCurrency(fs.taxable_value)}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            <div className="text-sm text-slate-500">{filteredClients.length} clients</div>
          </>
        )}
      </div>
    </Layout>
  );
};

export default Dashboard;
